// Reverse geocoding service using OLA Maps API
// Uses REACT_APP_OLA_API_KEY from your .env file
const cache = new Map(); // Cache to avoid repeated API calls

export const getStateFromCoordinates = async (latitude, longitude) => {
  // Check cache first
  const cacheKey = `${latitude.toFixed(4)},${longitude.toFixed(4)}`;
  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  try {
    const apiKey = process.env.REACT_APP_OLA_API_KEY;
    if (!apiKey) {
      console.warn(
        "OLA Maps API key not found. Using REACT_APP_OLA_API_KEY from .env"
      );
      return null;
    }

    const response = await fetch(
      `https://api.olamaps.io/places/v1/reverse-geocode?latlng=${latitude},${longitude}&api_key=${apiKey}`
    );

    if (!response.ok) {
      throw new Error(`OLA Reverse Geocoding API error: ${response.status}`);
    }

    const data = await response.json();

    if (data.results && data.results.length > 0) {
      // Find state from address components
      const result = data.results[0];
      
      // OLA Maps returns address_components with different types
      // Look for admin_1 or state level information
      let state = null;

      if (result.address_components) {
        const stateComponent = result.address_components.find(
          (component) => 
            component.types?.includes("administrative_area_level_1") ||
            component.types?.includes("admin_1")
        );
        state = stateComponent ? stateComponent.long_name : null;
      }

      // Fallback: try to extract state from formatted_address
      if (!state && result.formatted_address) {
        const parts = result.formatted_address.split(",");
        // State is usually second to last part in Indian addresses
        if (parts.length >= 2) {
          state = parts[parts.length - 2].trim();
        }
      }

      cache.set(cacheKey, state);
      return state;
    }

    return null;
  } catch (error) {
    console.error("Error getting state from coordinates:", error);
    return null;
  }
};

// Batch process multiple vehicles to get their states
export const getStatesForVehicles = async (vehicles) => {
  const vehiclesWithStates = await Promise.all(
    vehicles.map(async (vehicle) => {
      if (!vehicle.latitude || !vehicle.longitude) {
        return { ...vehicle, state: "Unknown" };
      }

      const state = await getStateFromCoordinates(
        vehicle.latitude,
        vehicle.longitude
      );
      return { ...vehicle, state: state || "Unknown" };
    })
  );

  return vehiclesWithStates;
};

// Get unique states from vehicles
export const getUniqueStates = (vehicles) => {
  const states = vehicles
    .map((vehicle) => vehicle.state)
    .filter((state) => state && state !== "Unknown");
  return [...new Set(states)].sort();
};